/**
 * @module public/js/core/firebase-long-polling-toggle
 * @summary Utilities for enabling or disabling the Firestore long-polling override.
 *
 * @description
 * Provides a single helper (`tsForceLongPolling`) that mirrors the devtools helper exposed
 * by `firebase-service.js`. It persists the override flag to `localStorage`, keeps the
 * `forceLP=1` query parameter in sync when disabling, and triggers a page reload so the
 * updated transport settings take effect on the next boot.
 */

/**
 * Query-string matcher for the `forceLP=1` toggle.
 * @type {RegExp}
 */
export const FORCE_LONG_POLLING_QUERY = /(?:^|[?&])forceLP=1(?:&|$)/;

/**
 * Persist and synchronise the forced long-polling override, then reload the page.
 *
 * @param {boolean} [enable=true] Whether the override should be enabled on next load.
 * @param {Object} [dependencies] Injection points for easier testing (all optional).
 * @param {Storage} [dependencies.storage] Custom storage implementation (defaults to `localStorage`).
 * @param {Location} [dependencies.location] Location-like object to inspect and reload (defaults to `location`).
 * @param {History} [dependencies.history] History implementation used to rewrite the URL (defaults to `history`).
 * @param {Function} [dependencies.reload] Explicit reload function (defaults to `location.reload`).
 */
export function tsForceLongPolling(enable = true, dependencies = {}) {
  const storage =
    dependencies.storage ??
    (typeof localStorage !== 'undefined' ? localStorage : undefined);
  const locationLike =
    dependencies.location ??
    (typeof location !== 'undefined' ? location : undefined);
  const historyLike =
    dependencies.history ??
    (typeof history !== 'undefined' ? history : undefined);
  const reloadFn =
    dependencies.reload ??
    (locationLike && typeof locationLike.reload === 'function'
      ? locationLike.reload.bind(locationLike)
      : undefined);

  if (!storage || !locationLike) {
    return;
  }

  try {
    storage.setItem('ts_force_lp', enable ? '1' : '0');
  } catch (_) {
    return;
  }

  const shouldStripParam =
    !enable && FORCE_LONG_POLLING_QUERY.test(locationLike.search ?? '');
  if (
    shouldStripParam &&
    historyLike &&
    typeof historyLike.replaceState === 'function'
  ) {
    try {
      const url = toUrl(locationLike);
      url.searchParams.delete('forceLP');
      historyLike.replaceState(null, '', url.toString());
    } catch (_) {
      // Ignore URL parsing issues; the reload will still apply the storage flag.
    }
  }

  try {
    if (typeof reloadFn === 'function') {
      reloadFn();
    }
  } catch (_) {
    // Swallow reload errors to avoid breaking dev workflows.
  }
}

/**
 * Construct a URL instance from a location-like object.
 * @param {Location} loc The location-like object.
 * @returns {URL}
 */
function toUrl(loc) {
  const origin = loc.origin ?? 'http://localhost';
  const href =
    loc.href ?? `${loc.pathname ?? '/'}${loc.search ?? ''}${loc.hash ?? ''}`;
  return new URL(href, origin);
}
